import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Popover,
  Button,
  CircularProgress,
} from "@mui/material";
import axiosClient from "../../../../axios-client"; // Adjust path as needed

const BusinessClicks = ({ businessList }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [clickCounts, setClickCounts] = useState({
    phone: null,
    message: null,
    hire: null,
    virtualCall: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle Popover open/close
  const handleOpenPopover = async (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
    setLoading(true);
    setError(null);
    try {
      // Fetch counts for all click types
      const [
        phoneResponse,
        messageResponse,
        hireResponse,
        virtualCallResponse,
      ] = await Promise.all([
        fetchClicks(row.Id, 1), // clickType 1: Call
        fetchClicks(row.Id, 2), // clickType 2: Message
        fetchClicks(row.Id, 4), // clickType 3: Hire
        fetchClicks(row.Id, 5), // clickType 4: Virtual Call
      ]);
      setClickCounts({
        phone: phoneResponse.data,
        message: messageResponse.data,
        hire: hireResponse.data,
        virtualCall: virtualCallResponse.data,
      });
    } catch (err) {
      setError("Failed to fetch click counts");
      setClickCounts({
        phone: null,
        message: null,
        hire: null,
        virtualCall: null,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setSelectedRow(null);
    setClickCounts({
      phone: null,
      message: null,
      hire: null,
      virtualCall: null,
    });
    setError(null);
  };

  const open = Boolean(anchorEl);

  // Fetch click count for a specific postId and clickType
  const fetchClicks = async (postId, clickType) => {
    const form = new FormData();
    form.append("postId", postId);
    form.append("clickType", clickType);
    try {
      const response = await axiosClient.get(
        `message/CountPhoneMessageClicks`,
        {
          params: {
            postId,
            clickType,
          },
        }
      );
      if (response.data.statusCode === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || "Error fetching click count");
      }
    } catch (error) {
      throw new Error(
        `Error fetching click count for type ${clickType}: ${error.message}`
      );
    }
  };

  // Define table columns
  const cellStyles = {
    default: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    header: {
      fontWeight: "bold",
      backgroundColor: "#f5f5f5",
    },
    vendorName: {
      minWidth: 150,
      maxWidth: 200,
    },
    vendorEmail: {
      minWidth: 150,
      maxWidth: 200,
    },
    title: {
      minWidth: 150,
      maxWidth: 200,
    },
    report: {
      minWidth: 150,
      maxWidth: 200,
    },
  };

  // Combine styles for header and body cells
  const headerCellStyles = (variant) => ({
    ...cellStyles.default,
    ...cellStyles.header,
    ...cellStyles[variant],
  });

  const bodyCellStyles = (variant) => ({
    ...cellStyles.default,
    ...cellStyles[variant],
  });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {businessList && businessList.length > 0 ? (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="business clicks table">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyles("vendorName")}>
                  Vendor Name
                </TableCell>
                <TableCell sx={headerCellStyles("vendorEmail")}>
                  Vendor Email
                </TableCell>
                <TableCell sx={headerCellStyles("title")}>Title</TableCell>
                <TableCell sx={headerCellStyles("report")}>Reports</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {businessList
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow key={row.Id}>
                    <TableCell
                      sx={bodyCellStyles("vendorName")}
                      title={row.FullName}
                    >
                      {row.FullName || "N/A"}
                    </TableCell>
                    <TableCell
                      sx={bodyCellStyles("vendorEmail")}
                      title={row.Email}
                    >
                      {row.Email || "N/A"}
                    </TableCell>
                    <TableCell
                      sx={bodyCellStyles("title")}
                      title={row.listName}
                    >
                      {row.listName || "N/A"}
                    </TableCell>
                    <TableCell sx={bodyCellStyles("report")}>
                      <Button
                        variant="text"
                        sx={{
                          textTransform: "none",
                          color: "#1976d2",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          padding: 0,
                          minWidth: "auto",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={(event) => handleOpenPopover(event, row)}
                        aria-describedby={
                          open && selectedRow?.Id === row.Id
                            ? `popover-${row.Id}`
                            : undefined
                        }
                      >
                        Click to View
                      </Button>
                      <Popover
                        id={
                          open && selectedRow?.Id === row.Id
                            ? `popover-${row.Id}`
                            : undefined
                        }
                        open={open && selectedRow?.Id === row.Id}
                        anchorEl={anchorEl}
                        onClose={handleClosePopover}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        sx={{
                          "& .MuiPopover-paper": {
                            padding: "16px",
                            minWidth: "200px",
                            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                          },
                        }}
                      >
                        {loading ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              p: 2,
                            }}
                          >
                            <CircularProgress size={24} />
                          </Box>
                        ) : error ? (
                          <Typography variant="body2" color="error">
                            {error}
                          </Typography>
                        ) : (
                          <>
                            <Typography variant="body2">
                              <strong>Phone Clicks:</strong>{" "}
                              {clickCounts.phone !== null
                                ? clickCounts.phone
                                : "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Message Clicks:</strong>{" "}
                              {clickCounts.message !== null
                                ? clickCounts.message
                                : "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Hire Clicks:</strong>{" "}
                              {clickCounts.hire !== null
                                ? clickCounts.hire
                                : "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Virtual Call Clicks:</strong>{" "}
                              {clickCounts.virtualCall !== null
                                ? clickCounts.virtualCall
                                : "N/A"}
                            </Typography>
                          </>
                        )}
                      </Popover>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={businessList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          />
        </TableContainer>
      ) : (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
          No business data available.
        </Typography>
      )}
    </Box>
  );
};

export default BusinessClicks;
